import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { GlassCard } from '@/components/ui/card';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

interface KpiCardProps {
  label: string;
  value: number;
  format?: 'number' | 'percent' | 'currency' | 'ms' | 'duration';
  delta?: number; // 0..1 for percent change vs previous period
  hint?: string;
  trend?: number[]; // mini sparkline
  glow?: 'critical' | 'high' | 'medium' | 'low' | undefined;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}

/**
 * KPI card - animated counter, mini sparkline, delta arrow, optional glow ring.
 *
 * 2026-05-26 rewrite: previously used Motion's `useMotionValue` +
 * `useTransform` + `animate`, which relies on Motion's own subscriber
 * tracking to update the rendered text WITHOUT going through React's
 * render cycle. That worked on most pages but failed on the home page
 * - value prop changed (verified via debug span) but the rendered
 * text stayed at the initial value. Suspected cause: duplicate
 * motion-library instances between the shell and federated remotes
 * decoupling the subscriber. Whatever the cause, relying on motion to
 * write to the DOM is fragile.
 *
 * This implementation tweens via React state + requestAnimationFrame
 * - guaranteed React-controlled rendering, so the number always
 * matches the current `value` prop no matter how the rest of the tree
 * is wired.
 */
export function KpiCard({
  label,
  value,
  format = 'number',
  delta,
  hint,
  trend,
  glow,
  icon: Icon,
  className,
}: KpiCardProps) {
  const [displayed, setDisplayed] = useState<number>(value);
  const rafRef = useRef<number | null>(null);

  // Track the previous value across renders so the tween knows where
  // to start from. Using a ref (not state) so reading it doesn't
  // schedule a re-render.
  const fromRef = useRef<number>(value);

  // Snap to value synchronously when it changes - guarantees the
  // rendered number always matches the prop, even if the RAF tween
  // is delayed, dropped, or interrupted. The tween below then
  // overrides this briefly to animate from the previous value, but
  // either way the final state is `value`.
  useEffect(() => {
    setDisplayed(value);
  }, [value]);

  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    const dur = 1.4 * 1000;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const v = from + (to - from) * ease(t);
      setDisplayed(t === 1 ? to : v);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Remember the target so the NEXT tween starts where this one
      // left off (not from the displayed mid-tween value).
      fromRef.current = to;
    };
  }, [value]);

  return (
    <GlassCard glow={glow} shine className={cn('p-5 flex flex-col gap-2', className)}>
      <div className="flex items-start justify-between">
        <div className="space-y-0.5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          {hint && <p className="text-[11px] text-muted-foreground/70">{hint}</p>}
        </div>
        {Icon && (
          <div className="h-8 w-8 rounded-md bg-gradient-to-br from-chart-1/20 to-chart-2/20 flex items-center justify-center text-primary">
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="text-3xl font-semibold tabular tracking-tight font-display">
        {formatValue(displayed, format)}
      </div>
      <div className="flex items-center justify-between">
        {delta !== undefined && (
          <div className={cn(
            'flex items-center gap-1 text-xs font-medium',
            delta > 0 ? 'text-success' : delta < 0 ? 'text-destructive' : 'text-muted-foreground',
          )}>
            {delta > 0 ? <ArrowUpRight className="h-3 w-3" /> : delta < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            <span>{Math.abs(delta * 100).toFixed(1)}%</span>
            <span className="text-muted-foreground font-normal">vs 30d</span>
          </div>
        )}
        {trend && trend.length > 1 && <Sparkline data={trend} />}
      </div>
    </GlassCard>
  );
}

function Sparkline({ data }: { data: number[] }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 80;
  const h = 22;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x},${y}`;
    })
    .join(' ');
  const last = data[data.length - 1]!;
  const lx = w;
  const ly = h - ((last - min) / range) * h;
  return (
    <svg width={w} height={h} className="opacity-90 shrink-0">
      <defs>
        <linearGradient id="sparkline-grad" x1="0" y1="0" x2="0" y2={h} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`0,${h} ${points} ${w},${h}`} fill="url(#sparkline-grad)" stroke="none" />
      <polyline points={points} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r="2" fill="hsl(var(--primary))" />
    </svg>
  );
}

function formatValue(v: number, format: KpiCardProps['format']): string {
  switch (format) {
    case 'percent':
      return `${(v * 100).toFixed(1)}%`;
    case 'currency':
      return `₹${Math.round(v).toLocaleString('en-IN')}`;
    case 'ms':
      return `${Math.round(v)}ms`;
    case 'duration':
      return formatDuration(v);
    case 'number':
    default:
      return Math.round(v).toLocaleString('en-IN');
  }
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}
